import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import ActionSettings from '../../../library/SimpleControls/ActionSettings';
import CompanyListingService from './CompanyListingService';

interface CompanyGridProps {
    companyList: any[];
    setCompanyList: (list: any[]) => void;
}

const FLAG_LABELS: Record<string, string> = {
    fortune_five_hundred: 'Fortune 500',
    startup: 'Startup',
    unicorn_status: 'Unicorn',
    mnc_status: 'MNC',
    listed_status: 'Listed',
    is_defence: 'Defence',
    is_prestigious: 'Prestigious',
    // tier_one: 'Tier 1',
    is_registered_company: 'Registered Company',
    is_indian_government: 'Indian Government',
    is_international_government: 'International Government',
    is_international_body: 'International Body',
    is_law_firm: 'Law Firm',
    is_courts_tribunals: 'Courts and Tribunals',
    is_educational_institute: 'Educational Institute',
    is_healthcare_organization: 'Healthcare Organization',
    is_ngo: 'NGO',
};

const BATCH_SIZE = 10;

const CompanyGrid = ({ companyList, setCompanyList }: CompanyGridProps) => {
    const navigate = useNavigate();
    const { globalState } = useContext(GlobalContext);
    const industryDomain: any[] = (globalState as any)?.domain?.get?.('INDUSTRY_CODE') ?? [];
    const countryDomain: any[] = (globalState as any)?.domain?.get?.('COUNTRY_CODE') ?? [];

    const getIndustryName = (row: any): string => {
        if (row.industry_id) {
            const match = industryDomain.find((d: any) => String(d.code) === String(row.industry_id));
            if (match) return match.value;
        }
        return row.industry ?? '-';
    };

    const resolveCountryName = (value: any): string => {
        if (!value) return '';
        // If the stored value is purely numeric it's a raw ID — look up the name
        if (!isNaN(Number(value)) && String(value).trim() !== '') {
            const match = countryDomain.find((d: any) => String(d.code) === String(value));
            return match ? match.value : '';   // hide unresolvable numeric IDs
        }
        return String(value);
    };
    const [visibleList, setVisibleList] = useState<any[]>(companyList.slice(0, BATCH_SIZE));
    const loadMoreRef = useRef<HTMLTableRowElement>(null);

    // Reset visible list whenever the full companyList changes (search / clear / delete)
    useEffect(() => {
        setVisibleList(companyList.slice(0, BATCH_SIZE));
    }, [companyList]);

    // IntersectionObserver: append next batch when sentinel row enters viewport
    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            if (companyList.length <= visibleList.length) {
                if (loadMoreRef.current) observer.unobserve(loadMoreRef.current);
                return;
            }
            if (entries[0].isIntersecting) {
                setVisibleList(companyList.slice(0, visibleList.length + BATCH_SIZE));
            }
        });

        if (loadMoreRef.current) observer.observe(loadMoreRef.current);
        return () => { if (loadMoreRef.current) observer.unobserve(loadMoreRef.current); };
    }, [loadMoreRef, visibleList, companyList]);

    const handleAction = (id: any, action: string) => {
        switch (action) {
            case 'EDIT':
                navigate(`/company/${id}/basic-info`);
                break;
            case 'VIEW':
                navigate(`/company/${id}/view`);
                break;
            case 'DELETE':
                handleDelete(id);
                break;
        }
    };

    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This will permanently delete this company record.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            await CompanyListingService.delete(id);
            setCompanyList(companyList.filter((c: any) => c.id !== id));
            Swal.fire({
                icon: 'success',
                title: 'Deleted!',
                text: 'Company deleted successfully.',
                timer: 1500,
                showConfirmButton: false,
            });
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete company. Please try again.' });
        }
    };

    const getActiveFlagBadges = (row: any) => {
        return Object.entries(FLAG_LABELS)
            .filter(([key]) => Number(row[key]) === 1)
            .map(([key, label]) => (
                <span key={key} className="badge bg-success me-1 font-11" style={{ fontSize: '11px' }}>
                    {label}
                </span>
            ));
    };

    return (
        <div className="ActiveListings_present">
            <div className="table-responsive attendee_dtl">
                <table className="table" style={{ minWidth: '1000px' }}>
                    <thead>
                        <tr>
                            <th>S.NO</th>
                            <th>Logo</th>
                            <th>Company Name</th>
                            <th>Country</th>
                            <th>Industry</th>
                            <th>Company Size</th>
                            <th>Attributes</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {companyList.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="text-center py-5 text-muted font-14">
                                    No companies found. Click <strong>Add Company</strong> to get started.
                                </td>
                            </tr>
                        ) : (
                            <>
                                {visibleList.map((row: any, index: number) => (
                                    <tr key={row.id}>
                                        <td>{index + 1}</td>
                                        <td>
                                            {row.logo ? (
                                                <img
                                                    src={row.logo}
                                                    alt={row.name}
                                                    style={{
                                                        width: 44,
                                                        height: 44,
                                                        objectFit: 'contain',
                                                        borderRadius: 6,
                                                        border: '1px solid #eee',
                                                        padding: 3,
                                                        background: '#fff',
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    style={{
                                                        width: 44,
                                                        height: 44,
                                                        background: '#f0f0f0',
                                                        borderRadius: 6,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: 18,
                                                        color: '#aaa',
                                                    }}>
                                                    🏢
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div className="font-14 font-500">{row.name}</div>
                                            {row.website && (
                                                <a
                                                    href={row.website.startsWith('http') ? row.website : `https://${row.website}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-12 text-sky-blue d-block text-truncate"
                                                    style={{ maxWidth: 180 }}>
                                                    {row.website}
                                                </a>
                                            )}
                                        </td>
                                        <td className="font-14">
                                            <div>{resolveCountryName(row.country) || '-'}</div>
                                            {row.headquarters_country && row.headquarters_country !== row.country && (
                                                <small className="text-muted">HQ: {resolveCountryName(row.headquarters_country) || row.headquarters_country}</small>
                                            )}
                                        </td>
                                        <td className="font-14">{getIndustryName(row)}</td>
                                        <td className="font-14">{row.company_size ?? '-'}</td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1">
                                                {getActiveFlagBadges(row)}
                                            </div>
                                        </td>
                                        <td>
                                            <ActionSettings
                                                id={row.id}
                                                actions={['Edit', 'View', 'Delete']}
                                                onClick={handleAction}
                                            />
                                        </td>
                                    </tr>
                                ))}
                                {/* Sentinel row — triggers loading the next batch */}
                                <tr ref={loadMoreRef} />
                            </>
                        )}
                    </tbody>
                </table>
                {visibleList.length < companyList.length && (
                    <div className="text-center py-2 text-muted font-13">
                        Showing {visibleList.length} of {companyList.length} companies…
                    </div>
                )}
            </div>
        </div>
    );
};

export default CompanyGrid;
